import {
  addMonths,
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  Filters,
  formatDate,
  Header,
  NotApplicable,
  ScrollContainer,
  Select,
  useBreakpoints,
  useLoading,
  useLocalStorageState,
  ValueOpt,
  StatsTeam,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAdminSuiteRequests } from "../../../api/RequesTixApi";
import AdminSuiteRequestGame from "../../../components/requests/adminSuite/AdminSuiteRequestGame";
import LocalStorageConstants from "../../../constants/LocalStorageConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { GamePreviewDTO } from "../../../types/Game";
import { AdminSuiteRequestsDTO, SuiteRequestDTO, SuiteRequestStatus } from "../../../types/SuiteRequest";
import { getTeamOption } from "../../../util/TeamUtil";

interface AdminSuiteRequestsFilters {
  startDate: string;
  endDate: string;
  homeTeamId: string | undefined;
  statusId: string | undefined;
}

const today = new Date();
const nextMonth = addMonths(today, 1);
const todayStr = formatDate(today, DATE_FORMATTER_STR);
const nextMonthStr = formatDate(nextMonth, DATE_FORMATTER_STR);

const DEFAULT_FILTERS: AdminSuiteRequestsFilters = {
  startDate: todayStr,
  endDate: nextMonthStr,
  homeTeamId: undefined,
  statusId: undefined,
};

const AdminSuiteRequests = () => {
  const { setLoading } = useLoading();
  const { isMobile } = useBreakpoints();
  const { teamsOptions, suiteRequestStatusOptions } = useDropdowns();
  const [adminSuiteRequests, setAdminSuiteRequests] = useState<AdminSuiteRequestsDTO | undefined>(undefined);

  const [filters, setFilters, localStorageLoaded] = useLocalStorageState<AdminSuiteRequestsFilters>(
    LocalStorageConstants.ADMIN_SUITE_REQUESTS_FILTERS,
    DEFAULT_FILTERS,
  );

  const { startDate, endDate, homeTeamId, statusId } = filters;

  const setFilter = useCallback(
    <K extends keyof AdminSuiteRequestsFilters>(key: K, value: AdminSuiteRequestsFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters],
  );

  const homeTeam: ValueOpt<StatsTeam> | undefined = useMemo(
    () => getTeamOption(homeTeamId, teamsOptions),
    [homeTeamId, teamsOptions],
  );
  const suiteStatus: ValueOpt<SuiteRequestStatus> | undefined = statusId
    ? suiteRequestStatusOptions.find(
        (opt: ValueOpt<SuiteRequestStatus>) => opt.value.suiteStatusId === parseInt(statusId),
      )
    : undefined;

  const dateRange: DateRange<Date> = {
    start: new Date(Date.parse(startDate!)),
    end: new Date(Date.parse(endDate!)),
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res: AdminSuiteRequestsDTO = await fetchAdminSuiteRequests(
        startDate!,
        endDate!,
        homeTeamId ? Number(homeTeamId) : undefined,
        statusId ? Number(statusId) : undefined,
      );
      setAdminSuiteRequests(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, homeTeamId, statusId, setLoading]);

  const updateDate = useCallback(
    (dateRange: DateRange<Date> | NotApplicable) => {
      const dr = dateRange as DateRange<Date>;
      setFilters((prev) => ({
        ...prev,
        startDate: formatDate(dr.start, DATE_FORMATTER_STR),
        endDate: formatDate(dr.end, DATE_FORMATTER_STR),
      }));
    },
    [setFilters],
  );

  const refreshData = useCallback(() => {
    if (!localStorageLoaded) {
      return;
    }
    void fetchRequests();
  }, [localStorageLoaded, fetchRequests]);

  useEffect(() => {
    if (!localStorageLoaded) {
      return;
    }
    void fetchRequests();
  }, [localStorageLoaded, fetchRequests]);

  return (
    <div>
      <Header sticky title="Suite Requests" flexColumn>
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 3}>
          <DateRangeInput
            key={localStorageLoaded ? "suite-requests-date" : "suite-requests-date-pending"}
            id="suite-requests-date"
            label="Date"
            value={dateRange}
            onChange={updateDate}
            maxRange={{
              months: 1,
            }}
            withPortal
            gutterBottom
            popoverPlacement="bottom-end"
          />
          <Select
            id="home-team"
            label="Home Team"
            value={homeTeam}
            options={teamsOptions}
            onChange={(value: ValueOpt<StatsTeam>) =>
              setFilter("homeTeamId", value?.value?.id ? String(value.value.id) : undefined)
            }
            placeholder="select a home team"
            gutterBottom
          />
          <Select
            id="status"
            label="Status"
            value={suiteStatus}
            options={suiteRequestStatusOptions}
            onChange={(value: ValueOpt<SuiteRequestStatus>) =>
              setFilter("statusId", value?.value?.suiteStatusId ? String(value.value.suiteStatusId) : undefined)
            }
            placeholder="Status"
            clearable
            gutterBottom
          />
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        {!!adminSuiteRequests &&
          adminSuiteRequests.schedule.map((gamePk: number) => {
            const game: GamePreviewDTO = adminSuiteRequests.games[gamePk];
            const requests: SuiteRequestDTO[] = adminSuiteRequests.requests[gamePk];
            if (!!game && !!requests) {
              return <AdminSuiteRequestGame key={gamePk} game={game} requests={requests} refreshData={refreshData} />;
            }
            return null;
          })}
      </ScrollContainer>
    </div>
  );
};

export default AdminSuiteRequests;
